import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { collection, query, orderBy, onSnapshot } from 'firebase/firestore';
import { db } from '../../services/firebase';
import { NewsPost } from '../../types';

interface NewsContextType {
  posts: NewsPost[];
  loading: boolean;
  refreshNews: () => void;
}

const NewsContext = createContext<NewsContextType | undefined>(undefined);

export const useNews = () => {
  const context = useContext(NewsContext);
  if (!context) {
    throw new Error('useNews must be used within a NewsProvider');
  }
  return context;
};

interface NewsProviderProps {
  children: ReactNode;
}

export const NewsProvider: React.FC<NewsProviderProps> = ({ children }) => {
  const [posts, setPosts] = useState<NewsPost[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Standard query: all news, ordered by date
    const q = query(collection(db, 'news'), orderBy('date', 'desc'));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const fetchedPosts = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as NewsPost));

      // Client-side Sort: Pinned first, then by date desc
      // We keep this client-side sort logic here to maintain consistent ordering
      const sortedPosts = fetchedPosts.sort((a, b) => {
        if (a.isPinned === b.isPinned) {
          return new Date(b.date).getTime() - new Date(a.date).getTime();
        }
        return (b.isPinned ? 1 : 0) - (a.isPinned ? 1 : 0);
      });

      setPosts(sortedPosts);
      setLoading(false);
    }, (error) => {
      console.error("News Query Error:", error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const refreshNews = () => {
    // onSnapshot handles updates, but we expose this for consistency
  };

  return (
    <NewsContext.Provider value={{ posts, loading, refreshNews }}>
      {children}
    </NewsContext.Provider>
  );
};
